// ============================================
// usePracticeSession - Hook para Sesiones Guiadas
// ============================================
// Maneja el ciclo de vida completo de una sesión de práctica:
// carga del plan, navegación entre ejercicios, registro de progreso,
// y persistencia en Firestore.
// ============================================

import { useState, useCallback, useEffect, useRef } from "react";
import type { NoteName } from "../lib/guitarEngine";
import {
  type PracticeSessionPlan,
  type SessionProgress,
  type SessionExerciseResult,
  type SessionExercise,
  type SessionStatus,
  type ExerciseStatus,
  generateBeginnerSession,
  generateIntermediateSession,
  generateSessionFromTaskConfig,
} from "../lib/practiceSessionEngine";
import { type CheckResult } from "../lib/exerciseEngine";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useStudentAuth } from "./useStudentAuth";
import { useStudentStats } from "./useStudentStats";

const SESSION_RESULTS_COLLECTION = "bordona_sessions";

interface PracticeSessionState {
  /** Estado general de la sesión */
  status: SessionStatus;
  /** Plan de práctica cargado */
  plan: PracticeSessionPlan | null;
  /** Progreso actual */
  progress: SessionProgress | null;
  /** Índice del ejercicio actual */
  currentIndex: number;
  /** Ejercicio activo */
  currentExercise: SessionExercise | null;
  /** Estado del ejercicio actual */
  exerciseStatus: ExerciseStatus;
  /** Feedback del último check */
  feedback: CheckResult | null;
  /** Si está guardando en Firestore */
  isSaving: boolean;
  /** Error */
  error: string | null;
}

const initialProgress = (plan: PracticeSessionPlan): SessionProgress => ({
  planId: plan.id,
  currentIndex: 0,
  completed: {},
  totalPracticeMs: 0,
  startedAt: Date.now(),
  totalStars: 0,
  totalScore: 0,
});

export function usePracticeSession() {
  const { profile } = useStudentAuth();
  const { recordPracticeSession } = useStudentStats();
  const [state, setState] = useState<PracticeSessionState>({
    status: "idle",
    plan: null,
    progress: null,
    currentIndex: 0,
    currentExercise: null,
    exerciseStatus: "pending",
    feedback: null,
    isSaving: false,
    error: null,
  });

  const sessionStartRef = useRef<number>(0);
  const exerciseStartRef = useRef<number>(0);
  const exerciseAttemptsRef = useRef<number>(0);

  // ─── Cargar un plan ───

  const loadSession = useCallback((plan: PracticeSessionPlan) => {
    const progress = initialProgress(plan);
    setState({
      status: "ready",
      plan,
      progress,
      currentIndex: 0,
      currentExercise: plan.exercises[0] || null,
      exerciseStatus: "pending",
      feedback: null,
      isSaving: false,
      error: null,
    });
    sessionStartRef.current = Date.now();
  }, []);

  // ─── Generar y cargar sesión por defecto ───

  const loadDefaultSession = useCallback((level?: string, rootNote?: NoteName) => {
    const plan =
      level === "intermedio" || level === "avanzado"
        ? generateIntermediateSession()
        : generateBeginnerSession(rootNote);
    loadSession(plan);
  }, [loadSession]);

  // ─── Cargar sesión desde una tarea de Firebase ───

  const loadFromTask = useCallback(
    (taskType: string, config: Record<string, any>) => {
      const plan = generateSessionFromTaskConfig(config, taskType);
      loadSession(plan);
    },
    [loadSession]
  );

  // ─── Empezar la sesión ───

  const startSession = useCallback(() => {
    setState((prev) => ({
      ...prev,
      status: "in-progress",
      exerciseStatus: "active",
      currentIndex: 0,
      currentExercise: prev.plan?.exercises[0] || null,
    }));
    sessionStartRef.current = Date.now();
    exerciseStartRef.current = Date.now();
    exerciseAttemptsRef.current = 0;
  }, []);

  // ─── Ir al siguiente ejercicio ───

  const nextExercise = useCallback(() => {
    setState((prev) => {
      if (!prev.plan || !prev.progress) return prev;

      const nextIndex = prev.currentIndex + 1;
      if (nextIndex >= prev.plan.exercises.length) {
        // Sesión completada
        const elapsedMs = Date.now() - sessionStartRef.current;
        const finalProgress: SessionProgress = {
          ...prev.progress,
          currentIndex: nextIndex,
          totalPracticeMs: prev.progress.totalPracticeMs + elapsedMs,
          completedAt: Date.now(),
        };

        // Guardar en Firestore
        saveSessionToFirestore(finalProgress, prev.plan).catch(console.error);

        // Registrar estadísticas locales
        const completedCount = Object.values(finalProgress.completed).filter((r) => r.completed).length;
        recordPracticeSession(
          finalProgress.totalPracticeMs,
          completedCount,
          finalProgress.totalStars
        );

        return {
          ...prev,
          status: "completed" as SessionStatus,
          progress: finalProgress,
          currentExercise: null,
          exerciseStatus: "completed" as ExerciseStatus,
        };
      }

      const nextExercise = prev.plan.exercises[nextIndex];
      exerciseStartRef.current = Date.now();
      exerciseAttemptsRef.current = 0;

      return {
        ...prev,
        currentIndex: nextIndex,
        currentExercise: nextExercise,
        exerciseStatus: "active" as ExerciseStatus,
        feedback: null,
      };
    });
  }, []);

  // ─── Saltar ejercicio ───

  const skipExercise = useCallback(() => {
    setState((prev) => {
      if (!prev.plan || !prev.progress) return prev;

      const skippedResult: SessionExerciseResult = {
        stepId: prev.plan.exercises[prev.currentIndex]?.stepId || "",
        completed: false,
        stars: 0,
        score: 0,
        attempts: 0,
        timeMs: 0,
        feedback: "Saltado",
      };

      const updatedCompleted = {
        ...prev.progress.completed,
        [skippedResult.stepId]: skippedResult,
      };

      // Guardar skip en Firestore
      savePartialProgress({
        ...prev.progress,
        completed: updatedCompleted,
      }).catch(console.error);

      const nextIndex = prev.currentIndex + 1;
      if (nextIndex >= prev.plan.exercises.length) {
        return {
          ...prev,
          status: "completed" as SessionStatus,
          progress: {
            ...prev.progress,
            completed: updatedCompleted,
            completedAt: Date.now(),
          },
          currentExercise: null,
          exerciseStatus: "completed" as ExerciseStatus,
        };
      }

      exerciseStartRef.current = Date.now();
      exerciseAttemptsRef.current = 0;

      return {
        ...prev,
        currentIndex: nextIndex,
        currentExercise: prev.plan.exercises[nextIndex],
        exerciseStatus: "active" as ExerciseStatus,
        feedback: null,
        progress: { ...prev.progress, completed: updatedCompleted },
      };
    });
  }, []);

  // ─── Registrar resultado de un ejercicio ───

  const completeExercise = useCallback(
    (result: Omit<SessionExerciseResult, "stepId" | "timeMs" | "attempts">) => {
      setState((prev) => {
        if (!prev.plan || !prev.progress || !prev.currentExercise) return prev;

        const elapsedMs = Date.now() - exerciseStartRef.current;
        const attempts = exerciseAttemptsRef.current + 1;

        const exerciseResult: SessionExerciseResult = {
          stepId: prev.currentExercise.stepId,
          ...result,
          attempts,
          timeMs: elapsedMs,
        };

        const updatedCompleted = {
          ...prev.progress.completed,
          [exerciseResult.stepId]: exerciseResult,
        };

        const updatedProgress: SessionProgress = {
          ...prev.progress,
          completed: updatedCompleted,
          totalStars: prev.progress.totalStars + (result.stars || 0),
          totalScore: prev.progress.totalScore + (result.score || 0),
        };

        // Guardar progreso parcial
        savePartialProgress(updatedProgress).catch(console.error);

        return {
          ...prev,
          progress: updatedProgress,
          exerciseStatus: "completed" as ExerciseStatus,
          feedback: {
            correct: result.completed,
            message: result.feedback || "¡Bien hecho!",
            emoji: result.completed ? "🌟" : "💪",
            stars: result.stars || 0,
          },
        };
      });
    },
    []
  );

  // ─── Registrar intento (para feedback sin completar) ───

  const recordAttempt = useCallback(
    (feedback: CheckResult) => {
      exerciseAttemptsRef.current++;
      setState((prev) => ({
        ...prev,
        feedback,
      }));
    },
    []
  );

  // ─── Reiniciar sesión ───

  const resetSession = useCallback(() => {
    setState({
      status: "idle",
      plan: null,
      progress: null,
      currentIndex: 0,
      currentExercise: null,
      exerciseStatus: "pending",
      feedback: null,
      isSaving: false,
      error: null,
    });
    sessionStartRef.current = 0;
    exerciseStartRef.current = 0;
    exerciseAttemptsRef.current = 0;
  }, []);

  // ─── Persistencia en Firestore ───

  const savePartialProgress = async (progress: SessionProgress) => {
    if (!state.plan) return;
    try {
      const docId = `${progress.planId}_${profile?.uid || "anon"}`;
      await setDoc(doc(db, SESSION_RESULTS_COLLECTION, docId), {
        ...progress,
        planTitle: state.plan.title,
        totalExercises: state.plan.exercises.length,
        studentName: profile
          ? `${profile.firstName} ${profile.lastName}`
          : "Anónimo",
        studentUid: profile?.uid || "anon",
        updatedAt: serverTimestamp(),
        completed: false,
      });
    } catch (err) {
      console.error("Error saving session progress:", err);
    }
  };

  const saveSessionToFirestore = async (
    progress: SessionProgress,
    plan: PracticeSessionPlan
  ) => {
    setState((prev) => ({ ...prev, isSaving: true }));
    try {
      const docId = `${progress.planId}_${profile?.uid || "anon"}`;
      await setDoc(doc(db, SESSION_RESULTS_COLLECTION, docId), {
        ...progress,
        planTitle: plan.title,
        planDescription: plan.description,
        totalExercises: plan.exercises.length,
        studentName: profile
          ? `${profile.firstName} ${profile.lastName}`
          : "Anónimo",
        studentUid: profile?.uid || "anon",
        taskCode: plan.taskCode || null,
        submittedAt: serverTimestamp(),
        completed: true,
      });
    } catch (err) {
      console.error("Error saving session to Firestore:", err);
      setState((prev) => ({ ...prev, isSaving: false, error: "Error al guardar el progreso" }));
    } finally {
      setState((prev) => ({ ...prev, isSaving: false }));
    }
  };

  return {
    state,
    loadSession,
    loadDefaultSession,
    loadFromTask,
    startSession,
    nextExercise,
    skipExercise,
    completeExercise,
    recordAttempt,
    resetSession,
  };
}
